import { isUnknownArray } from "../types/data";
import type { FilmMetadata } from "../types/media";
import type { Rating } from "../types/rating";
import type { ActivityRecord } from "../types/activity";
import type { AchievementExperienceItem } from "../types/experience";
const QUOTED_TEXT_PATTERN = /[«"]([^»"]+)[»"]/;
const LIST_LABELS = new Set([
  "буду смотреть",
  "watchlist",
  "любимые фильмы",
  "favorite movies",
  "избранное",
  "favorites"
]);

const getMetadata = (item: ActivityRecord | null | undefined) =>
  item && typeof item.metadata === "object" && item.metadata ? item.metadata : {};

const getText = (...values: unknown[]) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const getLowerText = (...values: unknown[]) =>
  values.map((value) => String(value || "").toLowerCase()).join(" ");

const getNumber = (...values: unknown[]) => {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const GENERIC_FILM_TITLES = new Set([
  "film",
  "movie",
  "series",
  "tv show",
  "фильм",
  "сериал"
]);

const normalizeFilmTitle = (value: unknown) =>
  String(value || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[«»"“”]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const getFilmIdentityTitles = (film: FilmMetadata | null | undefined) =>
  [
    film?.title,
    film?.filmTitle,
    film?.movieTitle,
    film?.name,
    film?.original_title,
    film?.original_name,
    film?.originalTitle,
    film?.originalName
  ]
    .map(normalizeFilmTitle)
    .filter((title) => title && !GENERIC_FILM_TITLES.has(title));

export const getFilmMediaType = (film: FilmMetadata | null | undefined) => {
  const explicitType = String(film?.media_type || film?.mediaType || film?.type || "").toLowerCase();
  if (explicitType === "tv" || explicitType === "series" || explicitType === "сериал") return "tv";
  if (explicitType === "movie" || explicitType === "film" || explicitType === "фильм") return "movie";
  if (film?.first_air_date || film?.firstAirDate || isUnknownArray(film?.seasons)) return "tv";
  return null;
};

export const buildFilmPagePath = (film: FilmMetadata | null | undefined) => {
  const id = getText(film?.id, film?.filmId, film?.movieId, film?.tmdbId);
  if (!id) return "";
  const encodedId = encodeURIComponent(String(id));
  const mediaType = getFilmMediaType(film);
  return mediaType
    ? `/film-page/${mediaType}/${encodedId}`
    : `/film-page/${encodedId}`;
};

// Rating documents are also used as convenient film metadata in statistics,
// activity and friends' profiles. None of the account-scoped fields may cross
// that navigation boundary: FilmPage must resolve the signed-in user's rating
// from UserRatingsContext instead of inheriting another profile's state.
const ACCOUNT_SCOPED_FILM_FIELDS = new Set([
  "userId",
  "userRate",
  "rating",
  "detailedRate",
  "seasonRatings",
  "watchedOnly",
  "ratedAt",
  "firstRatedAt",
  "firstTrackedAt",
  "lastRatedAt",
  "updatedAt",
  "deletedAt",
  "active",
  "ratingLifecycleId",
  "progressUserRate",
  "progressMode",
  "firstProgressMode",
  "streakEligible",
  "firstStreakEligible",
  "weekQualityEligibleAtFirstRating",
  "weekQualityWeekKey",
  "manualWatchedAt",
  "importSource"
]);

const withoutAccountScopedFilmFields = (film: FilmMetadata = {}): FilmMetadata =>
  Object.fromEntries(
    Object.entries(film).filter(([key]) => !ACCOUNT_SCOPED_FILM_FIELDS.has(key))
  );

const extractQuotedText = (...values: unknown[]) => {
  for (const value of values) {
    const quoted = String(value || "").match(QUOTED_TEXT_PATTERN)?.[1];
    if (quoted) return quoted.trim();
  }
  return "";
};

const isListLabel = (value: string) => LIST_LABELS.has(String(value || "").trim().toLowerCase());

export const buildAchievementElementId = (achievement: AchievementExperienceItem | null | undefined) =>
  `achievement-${String(achievement?.id || achievement?.title || "")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}_-]+/gu, "-")
    .replace(/^-+|-+$/g, "")}`;

export const extractAchievementIdentifier = (item: ActivityRecord | null | undefined) => {
  const metadata = getMetadata(item);
  const direct = getText(metadata.achievementId, metadata.achievementTitle, metadata.title);
  if (direct) return direct;

  const key = String(item?.key || "");
  if (key.startsWith("achievement:")) return key.slice("achievement:".length);

  const title = String(item?.title || item?.message || "");
  const description = String(item?.description || "");
  const titleAfterColon = title.match(/^Новое достижение:\s*(.+)$/i)?.[1];
  if (titleAfterColon) return titleAfterColon.trim();

  const quoted = title.match(QUOTED_TEXT_PATTERN)?.[1] || description.match(QUOTED_TEXT_PATTERN)?.[1];
  return quoted ? quoted.trim() : "";
};

const extractFriendNameFromText = (...values: unknown[]) => {
  const value = values.map((entry) => String(entry || "").trim()).find(Boolean) || "";
  const addedFriend = value.match(/^(.+?)\s+добавлен[ао]?\s+в\s+друзья/i)?.[1];
  if (addedFriend) return addedFriend.trim();

  const markers = [
    " поставил",
    " получил",
    " завершил",
    " начал",
    " напис",
    " добав"
  ];
  const markerIndex = markers
    .map((marker) => value.indexOf(marker))
    .filter((index) => index > 0)
    .sort((a, b) => a - b)[0];
  return markerIndex ? value.slice(0, markerIndex).trim() : "";
};

const extractFriendIdFromKey = (item: ActivityRecord | null | undefined) => {
  const category = String(item?.category || "");
  const scope = String(item?.scope || "");
  if (scope !== "friend" && !category.startsWith("friend_")) return "";
  const key = String(item?.key || "");
  if (!key || key.startsWith("achievement:") || key.startsWith("experience:")) return "";
  const [candidate] = key.split(":");
  if (!candidate || candidate === key || candidate.length < 10) return "";
  return candidate;
};

const extractFilmIdFromKey = (item: ActivityRecord | null | undefined) => {
  const key = String(item?.key || "");
  if (!key) return "";
  const parts = key.split(":");
  const prefix = parts[0];

  if (prefix === "experience" && parts.length >= 3) return parts[2];
  if (prefix === "import-rating" && parts.length >= 2) return parts[1];
  if (["rating", "review", "favorite", "watchlist", "film"].includes(prefix) && parts.length >= 2) {
    return parts[1];
  }

  return "";
};

export const isFilmActivity = (item: ActivityRecord | null | undefined) => {
  const type = String(item?.type || "").toLowerCase();
  const category = String(item?.category || "").toLowerCase();
  const text = getLowerText(item?.title, item?.description, item?.message);

  return (
    ["rating", "review", "favorite", "watchlist", "experience"].includes(type) ||
    ["rating", "review", "favorite", "watchlist", "experience"].some((marker) => category.includes(marker)) ||
    /фильм|оценк|просмотр|реценз/.test(text)
  );
};

export const getFriendNameCandidate = (item: ActivityRecord | null | undefined) => {
  const metadata = getMetadata(item);
  return getText(
    metadata.friendName,
    metadata.friendDisplayName,
    metadata.displayName,
    metadata.name,
    extractFriendNameFromText(item?.title),
    extractFriendNameFromText(item?.description),
    extractFriendNameFromText(item?.message)
  );
};

export const getFriendReference = (item: ActivityRecord | null | undefined) => {
  const metadata = getMetadata(item);
  const friendId = getText(
    metadata.friendId,
    metadata.friendUid,
    metadata.uid,
    metadata.userId,
    extractFriendIdFromKey(item)
  );
  const friendName = getFriendNameCandidate(item);

  if (!friendId) return null;
  return {
    id: friendId,
    name: friendName || "Друг"
  };
};

export const getActivityFilmTitle = (item: ActivityRecord | null | undefined) => {
  const metadata = getMetadata(item);
  const titleQuote = extractQuotedText(item?.title);
  const detailQuote = extractQuotedText(item?.description, item?.message);
  return getText(
    metadata.filmTitle,
    metadata.movieTitle,
    metadata.title,
    metadata.name,
    isListLabel(titleQuote) ? detailQuote : titleQuote,
    detailQuote
  );
};

export const getFilmReference = (item: ActivityRecord | null | undefined) => {
  const metadata = getMetadata(item);
  const directFilmId = getText(
    metadata.filmId,
    metadata.movieId,
    metadata.tmdbId,
    metadata.film_id,
    metadata.movie_id,
    metadata.tmdb_id
  );
  const filmId = getText(directFilmId, extractFilmIdFromKey(item));

  if (!filmId || (!directFilmId && !extractFilmIdFromKey(item) && !isFilmActivity(item))) {
    return null;
  }

  const filmTitle = getActivityFilmTitle(item);
  const mediaType = getText(
    metadata.mediaType,
    metadata.media_type,
    metadata.type,
    item?.mediaType,
    item?.media_type,
    item?.type
  );
  const normalizedMediaType = getFilmMediaType({
    type: mediaType,
    first_air_date: metadata.firstAirDate || metadata.first_air_date || item?.first_air_date,
    release_date: metadata.releaseDate || metadata.release_date || item?.release_date,
    seasons: metadata.seasons || item?.seasons
  });

  const reference: FilmMetadata & { id: string; title: string } = {
    id: filmId,
    filmId,
    title: filmTitle || "Фильм"
  };
  const optionalFields: Omit<FilmMetadata, "id" | "title"> = {
    titleEn: getText(metadata.titleEn, metadata.englishTitle, metadata.enTitle, item?.titleEn, item?.englishTitle, item?.enTitle),
    englishTitle: getText(metadata.englishTitle, metadata.titleEn, item?.englishTitle, item?.titleEn),
    name: getText(metadata.name, item?.name),
    original_title: getText(metadata.originalTitle, metadata.original_title, item?.original_title),
    original_name: getText(metadata.originalName, metadata.original_name, item?.original_name),
    release_date: getText(metadata.releaseDate, metadata.release_date, item?.release_date),
    first_air_date: getText(metadata.firstAirDate, metadata.first_air_date, item?.first_air_date),
    poster_path: getText(metadata.posterPath, metadata.poster_path, item?.poster_path),
    vote_average: getNumber(metadata.voteAverage, metadata.vote_average, item?.vote_average),
    vote_count: getNumber(metadata.voteCount, metadata.vote_count, item?.vote_count),
    mediaType: normalizedMediaType || undefined,
    media_type: normalizedMediaType || undefined
  };
  const assignField = <K extends keyof typeof optionalFields>(key: K) => {
    const value = optionalFields[key];
    if (value !== "" && value !== null && value !== undefined) Object.assign(reference, { [key]: value });
  };
  const optionalKeys = Object.keys(optionalFields) as (keyof typeof optionalFields)[];
  optionalKeys.forEach(assignField);
  if (!optionalKeys.some((key) => reference[key])) delete reference.filmId;
  return reference;
};

export const buildFilmNavigationPayload = (film: FilmMetadata | null | undefined): (FilmMetadata & { id: string; title: string }) | null => {
  if (!film?.id && !film?.filmId && !film?.movieId && !film?.tmdbId) return null;
  const id = getText(film.id, film.filmId, film.movieId, film.tmdbId);
  const title = getText(
    film.title,
    film.filmTitle,
    film.movieTitle,
    film.name,
    film.original_title,
    film.original_name,
    "Фильм"
  );
  const mediaType = getFilmMediaType(film);
  const publicFilmData = withoutAccountScopedFilmFields(film);

  return {
    ...publicFilmData,
    id,
    filmId: id,
    title,
    name: getText(film.name, film.title, film.filmTitle, title),
    original_title: getText(film.original_title, film.originalTitle, film.filmOriginalTitle),
    original_name: getText(film.original_name, film.originalName, film.filmOriginalTitle),
    release_date: getText(film.release_date, film.releaseDate, film.filmReleaseDate),
    first_air_date: getText(film.first_air_date, film.firstAirDate),
    poster_path: getText(film.poster_path, film.posterPath, film.filmPoster),
    vote_average: getNumber(film.vote_average, film.voteAverage, film.filmVoteAverage),
    vote_count: getNumber(film.vote_count, film.voteCount, film.filmVoteCount),
    ...(mediaType ? { mediaType, media_type: mediaType } : {})
  };
};

export const resolveFilmReferenceFromRatings = (film: FilmMetadata | null, ratings: Rating[] = []) => {
  if (!film?.id || getFilmMediaType(film)) return film;

  const matchingId = (isUnknownArray(ratings) ? ratings : []).filter(
    (rating: Rating) =>
      String(rating?.filmId || rating?.id || rating?.movieId || rating?.tmdbId || "") ===
      String(film.id)
  );
  if (!matchingId.length) return film;

  const referenceTitles = getFilmIdentityTitles(film);
  const titleMatch = matchingId.find((rating: Rating) =>
    getFilmIdentityTitles(rating).some((title) => referenceTitles.includes(title))
  );
  const onlyRating = matchingId.length === 1 ? matchingId[0] : null;
  const canUseIdOnlyMatch =
    onlyRating &&
    (!referenceTitles.length || !getFilmIdentityTitles(onlyRating).length);
  const rating = titleMatch || (canUseIdOnlyMatch ? onlyRating : null);
  const payload = rating ? buildFilmNavigationPayload(rating) : null;
  if (!payload || !getFilmMediaType(payload)) return film;

  return {
    ...payload,
    title: film.title || payload.title,
    filmTitle: film.filmTitle || payload.filmTitle || payload.title
  };
};

export const getActivityNavigationTarget = (item: ActivityRecord | null | undefined, viewedUserId: string | null | undefined) => {
  if (!item) return "";
  if (item.type === "collection_shared" || item.category === "social_collection_shared") {
    const sharedCollectionId = getText(
      item.metadata?.sharedCollectionId,
      item.sharedCollectionId
    );
    if (!viewedUserId) return "";
    const params = new URLSearchParams({ tab: "personal" });
    if (sharedCollectionId) params.set("shared", sharedCollectionId);
    return `/${viewedUserId}/collections?${params.toString()}`;
  }
  const friend = getFriendReference(item);
  const film = getFilmReference(item);
  const achievementIdentifier = extractAchievementIdentifier(item);

  if (achievementIdentifier && (item.type === "achievement" || String(item.category || "").includes("achievement"))) {
    const targetUserId = friend?.id || viewedUserId;
    const params = new URLSearchParams({
      tab: "achievements",
      achievement: String(achievementIdentifier)
    });
    return targetUserId ? `/${targetUserId}/achievements?${params.toString()}` : "";
  }

  if (film?.id && isFilmActivity(item)) {
    return buildFilmPagePath(film);
  }

  if (item.type === "dungeon" || String(item.category || "").includes("dungeon")) {
    const targetUserId = friend?.id || viewedUserId;
    const dungeonId = item.metadata?.dungeonId || item.dungeonId || "";
    const params = new URLSearchParams({ tab: "dungeons" });
    if (dungeonId) params.set("dungeon", String(dungeonId));
    return targetUserId ? `/${targetUserId}/game?${params.toString()}` : "";
  }

  if (item.type === "challenge" || String(item.category || "").includes("challenge")) {
    const targetUserId = friend?.id || viewedUserId;
    const challengeId = item.metadata?.challengeId || item.challengeId || "";
    const params = new URLSearchParams({ tab: "challenges" });
    if (challengeId) params.set("challenge", String(challengeId));
    return targetUserId ? `/${targetUserId}/game?${params.toString()}` : "";
  }

  if (item.type === "weekly_card" || String(item.category || "").includes("weekly_card")) {
    const targetUserId = friend?.id || viewedUserId;
    const cardId = item.metadata?.weeklyCardId || item.weeklyCardId || "";
    const params = new URLSearchParams({ tab: "cards" });
    if (cardId) params.set("card", String(cardId));
    return targetUserId ? `/${targetUserId}/game?${params.toString()}` : "";
  }

  if (item.type === "friend_added" && friend?.id) {
    return `/${friend.id}/profile`;
  }

  return "";
};
